import { useState, useCallback, useEffect, useRef } from 'react';
import { useKnowledgeService } from '../../knowledge/services/knowledgeService';
import { useFindingsService } from '../../findings/services/findingsService';
import { useInvestigationService } from '../services/investigationService';
import type { KnowledgeItem, Finding, InvestigationScope, Customer, ChatSession, InvestigationSummary } from '../../../types';

interface UseInvestigationOperationsProps {
  currentSession: ChatSession | null;
  selectedCustomer: Customer | null;
  sessions: InvestigationSummary[];
  setSessions: (sessions: InvestigationSummary[]) => void;
  setCurrentSession: (session: ChatSession) => void;
  refreshInvestigation: (investigationId: string) => Promise<void>;
  sendMessage: (message: string) => void;
}

export function useInvestigationOperations({
  currentSession,
  selectedCustomer,
  sessions,
  setSessions,
  setCurrentSession,
  refreshInvestigation,
  sendMessage,
}: UseInvestigationOperationsProps) {
  const investigationService = useInvestigationService();
  const knowledgeService = useKnowledgeService();
  const findingsService = useFindingsService();

  const [knowledgeItems, setKnowledgeItems] = useState<KnowledgeItem[]>([]);
  const [findings, setFindings] = useState<Finding[]>([]);
  const [error, setError] = useState<string | null>(null);
  const loadGenerationRef = useRef(0);
  // Latest (customer, investigation) the panel is showing. Mutation handlers
  // capture the key they were issued against and drop their refetch result if
  // the user has since switched investigation or customer.
  const activeInvestigationKeyRef = useRef<string | null>(null);
  useEffect(() => {
    activeInvestigationKeyRef.current =
      currentSession && selectedCustomer ? `${selectedCustomer.id}:${currentSession.id}` : null;
  }, [currentSession, selectedCustomer]);

  const loadInvestigationData = useCallback(async () => {
    const generation = ++loadGenerationRef.current;
    if (currentSession && selectedCustomer && currentSession.customer_id === selectedCustomer.id) {
      try {
        const investigationKnowledge = await knowledgeService.getKnowledgeForInvestigation(
          selectedCustomer.id,
          currentSession.id
        );
        if (generation !== loadGenerationRef.current) return;
        setKnowledgeItems(Array.isArray(investigationKnowledge) ? investigationKnowledge : []);

        const investigationFindings = await findingsService.getFindingsForInvestigation(
          selectedCustomer.id,
          currentSession.id
        );
        if (generation !== loadGenerationRef.current) return;
        setFindings(Array.isArray(investigationFindings) ? investigationFindings : []);
      } catch (err) {
        console.error('Failed to load investigation data:', err);
      }
    } else {
      setKnowledgeItems([]);
      setFindings([]);
    }
  }, [currentSession, selectedCustomer, knowledgeService, findingsService]);

  useEffect(() => {
    loadInvestigationData();
  }, [loadInvestigationData]);

  const handleCloseError = useCallback(() => {
    setError(null);
  }, []);

  const handleReorderKnowledge = useCallback(async (items: KnowledgeItem[]) => {
    setKnowledgeItems(items);
    if (currentSession && selectedCustomer) {
      try {
        await knowledgeService.updateKnowledgeRanking(selectedCustomer.id, currentSession.id, items);
      } catch (err) {
        console.error('Failed to update knowledge ranking:', err);
      }
    }
  }, [currentSession, selectedCustomer, knowledgeService]);

  const handleReorderFindings = useCallback(async (newFindings: Finding[]) => {
    setFindings(newFindings);
    if (currentSession && selectedCustomer) {
      try {
        await findingsService.updateFindingRanking(selectedCustomer.id, currentSession.id, newFindings);
      } catch (err) {
        console.error('Failed to update finding ranking:', err);
      }
    }
  }, [currentSession, selectedCustomer, findingsService]);

  const handleExecutePlaybook = useCallback(async () => {
    if (currentSession && selectedCustomer) {
      try {
        const updatedInvestigation = await investigationService.markPlaybookExecuted(selectedCustomer.id, currentSession.id);
        setSessions(sessions.map(s => s.id === currentSession.id ? updatedInvestigation : s));
        setCurrentSession(updatedInvestigation);
      } catch (err) {
        console.error('Failed to mark playbook as executed:', err);
        setError('Failed to execute playbook');
      }
    }
  }, [currentSession, selectedCustomer, investigationService, sessions, setSessions, setCurrentSession]);

  const handleRemoveKnowledgeItem = useCallback(async (itemId: string) => {
    if (!currentSession || !selectedCustomer) return;
    const investigationKey = `${selectedCustomer.id}:${currentSession.id}`;

    try {
      await knowledgeService.unlinkKnowledgeFromInvestigation(selectedCustomer.id, currentSession.id, [itemId]);
      const updatedKnowledge = await knowledgeService.getKnowledgeForInvestigation(selectedCustomer.id, currentSession.id);
      if (activeInvestigationKeyRef.current !== investigationKey) return;
      setKnowledgeItems(updatedKnowledge);
    } catch (err) {
      setError('Failed to remove knowledge item');
      console.error('Failed to remove knowledge item:', err);
    }
  }, [currentSession, selectedCustomer, knowledgeService]);

  const handleRemoveFinding = useCallback(async (findingId: string) => {
    if (!currentSession || !selectedCustomer) return;
    const investigationKey = `${selectedCustomer.id}:${currentSession.id}`;

    try {
      await findingsService.removeFinding(selectedCustomer.id, currentSession.id, findingId);
      const updatedFindings = await findingsService.getFindingsForInvestigation(selectedCustomer.id, currentSession.id);
      if (activeInvestigationKeyRef.current !== investigationKey) return;
      setFindings(updatedFindings);
    } catch (err) {
      setError('Failed to remove finding');
      console.error('Failed to remove finding:', err);
    }
  }, [currentSession, selectedCustomer, findingsService]);

  const handleAddAsFinding = useCallback(async (messageContent: string) => {
    if (!currentSession || !selectedCustomer) return;

    await sendMessage(`Add this as a finding to the investigation:\n\n${messageContent}`);
  }, [currentSession, selectedCustomer, sendMessage]);

  const handleToggleFindingRelevance = useCallback(async (findingId: string, irrelevant: boolean) => {
    if (!currentSession || !selectedCustomer) return;
    const investigationKey = `${selectedCustomer.id}:${currentSession.id}`;

    try {
      await findingsService.toggleFindingRelevance(selectedCustomer.id, currentSession.id, findingId, irrelevant);
      const updatedFindings = await findingsService.getFindingsForInvestigation(selectedCustomer.id, currentSession.id);
      if (activeInvestigationKeyRef.current !== investigationKey) return;
      setFindings(updatedFindings);
    } catch (err) {
      setError('Failed to toggle finding relevance');
      console.error('Failed to toggle finding relevance:', err);
    }
  }, [currentSession, selectedCustomer, findingsService]);

  const handleToggleFindingRootCause = useCallback(async (findingId: string, isPotentialRootCause: boolean) => {
    if (!currentSession || !selectedCustomer) return;
    const investigationKey = `${selectedCustomer.id}:${currentSession.id}`;

    try {
      await findingsService.togglePotentialRootCause(selectedCustomer.id, currentSession.id, findingId, isPotentialRootCause);
      const updatedFindings = await findingsService.getFindingsForInvestigation(selectedCustomer.id, currentSession.id);
      if (activeInvestigationKeyRef.current !== investigationKey) return;
      setFindings(updatedFindings);
    } catch (err) {
      setError('Failed to toggle finding root cause');
      console.error('Failed to toggle finding root cause:', err);
    }
  }, [currentSession, selectedCustomer, findingsService]);

  const handleUpdateFindingTags = useCallback(async (findingId: string, tags: string[]) => {
    if (!currentSession || !selectedCustomer) return;
    const investigationKey = `${selectedCustomer.id}:${currentSession.id}`;

    try {
      const updatedFindings = await findingsService.updateFinding(
        selectedCustomer.id, currentSession.id, findingId, { tags }
      );
      if (activeInvestigationKeyRef.current !== investigationKey) return;
      setFindings(updatedFindings);
    } catch (err) {
      setError('Failed to update finding tags');
      console.error('Failed to update finding tags:', err);
    }
  }, [currentSession, selectedCustomer, findingsService]);

  const handleDiveDeeper = useCallback((finding: Finding) => {
    const message = `I need more information about this finding:

**${finding.title}**

${finding.description}

Please provide more details and context about this finding.`;
    sendMessage(message);
  }, [sendMessage]);

  const handleMarkIrrelevant = useCallback((finding: Finding, reason: string) => {
    const message = `Mark this finding as irrelevant:

**${finding.title}**

Reason: ${reason}

Add the reason at the beginning of the finding description so it's immediately visible to the user.`;
    sendMessage(message);
  }, [sendMessage]);

  const handleManageScope = useCallback(async (investigationId: string, newScope: InvestigationScope[]) => {
    if (!selectedCustomer) return;

    try {
      await investigationService.updateInvestigation(selectedCustomer.id, investigationId, { scope: newScope });
      await refreshInvestigation(investigationId);
    } catch (err) {
      setError('Failed to update investigation scope');
      console.error('Failed to update investigation scope:', err);
    }
  }, [selectedCustomer, investigationService, refreshInvestigation]);

  const handleUpdateInvestigationMetadata = useCallback(async (investigationId: string, updates: { title?: string; description?: string }) => {
    if (!selectedCustomer) return;

    try {
      await investigationService.updateInvestigation(selectedCustomer.id, investigationId, updates);
      await refreshInvestigation(investigationId);
    } catch (err) {
      setError('Failed to update investigation metadata');
      console.error('Failed to update investigation metadata:', err);
    }
  }, [selectedCustomer, investigationService, refreshInvestigation]);

  const handleUpdateInvestigationStatus = useCallback(async (investigationId: string, state: 'active' | 'completed' | 'archived') => {
    if (!selectedCustomer) return;

    try {
      await investigationService.updateInvestigationStatus(selectedCustomer.id, investigationId, state);
      await refreshInvestigation(investigationId);
    } catch (err) {
      setError('Failed to update investigation state');
      console.error('Failed to update investigation state:', err);
    }
  }, [selectedCustomer, investigationService, refreshInvestigation]);

  return {
    knowledgeItems,
    findings,
    error,
    handleCloseError,
    loadInvestigationData,
    handleReorderKnowledge,
    handleReorderFindings,
    handleExecutePlaybook,
    handleRemoveKnowledgeItem,
    handleRemoveFinding,
    handleAddAsFinding,
    handleToggleFindingRelevance,
    handleToggleFindingRootCause,
    handleDiveDeeper,
    handleMarkIrrelevant,
    handleUpdateFindingTags,
    handleManageScope,
    handleUpdateInvestigationMetadata,
    handleUpdateInvestigationStatus,
  };
}
